import React, { useState, useEffect, useMemo, useCallback } from "react";
import FusionTheme from "@directv-digital/fusion.themes.fusion";
import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import TextField from "@mui/material/TextField";
import CardMedia from "@mui/material/CardMedia";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import HelperMessage from "@directv-digital/fusion.atoms.helper-message";
import { useClickOutside } from "@directv-digital/fusion.utils";
import { TextFieldDropdownProps } from "./text-field-dropdown.types"
import { textFieldDropdownStyles } from "./text-field-dropdown.styles";

const tabIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.key === 'Tab';
const enterIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.key === 'Enter';
const upArrowIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.key === 'ArrowUp';
const downArrowIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.key === 'ArrowDown';
const escIsPressed = (e: React.KeyboardEvent<HTMLElement>) => e.key === 'Escape';

export function TextFieldDropdown({
  id,
  mode = "light",
  size = "medium",
  width,
  error = false,
  label = "",
  disabled = false,
  options = [],
  onSelection = () => {
    Promise.resolve()
  },
  onBlur = () => {
    Promise.resolve()
  },
  onFocus = () => {
    Promise.resolve()
  },
  showHelperMessage = false,
  helperMessageText = "",
  helperMessageIcon = true,
  customHelperMessage = undefined,
  prefix = "",
  isLoadingOptions,
  isVisible = true,
  maxVisible,
  value,
  FormControlProps,
  TextFieldProps
}: TextFieldDropdownProps) {
  try {
    const dropdownLabelRef = React.useRef<HTMLInputElement>(null);
    const dropdownListRef = React.useRef<HTMLDivElement>(null);

    const [inputFocus, setInputFocus] = useState(false);
    const [open, setOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState<string>(value || options?.[0]?.value || "");

    const hideSelectionState = useMemo(() => options?.length === 1, [options]);

    const selectedValueLabel = useMemo(() => options?.find(option => option.value === value)?.label || "", [value, options]);

    const shrinkLabelActive = useMemo(() => (
      selectedValueLabel !== "" || open || inputFocus
    ), [selectedValueLabel, open, inputFocus, hideSelectionState]);

    const styles = React.useMemo(() => (
      textFieldDropdownStyles(size, mode, disabled, error, shrinkLabelActive)
    ), [size, mode, disabled, error, shrinkLabelActive]);

    console.log("styles...", styles)
    const blurField = () => {
      setInputFocus(false);
      setOpen(false);
      dropdownLabelRef?.current?.blur();
    }

    const focusField = () => {
      setInputFocus(true);
      setOpen(true);
      dropdownLabelRef?.current?.focus();
      onFocus();
    }

    const clickOutsideComponentRef = useClickOutside<HTMLDivElement>(() => {
      blurField();
      onBlur();
    });

    // Allow HOC control by passing the value explicitly
    useEffect(() => {
      setSelectedValue(value);
    }, [value, options]);

    // Ensure when error is triggered, it can be seen below field
    useEffect(() => {
      if (error) {
        setInputFocus(true);
        dropdownLabelRef?.current?.focus();
        setOpen(false);
      }
    }, [error]);

    const toggleSortingDropdown = () => {
      if (!open) {
        if (!disabled && options?.length > 0) {
          focusField();
        }
      } else {
        blurField();
      }
    }

    const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
      const target = e.target as HTMLElement;
      const labelRef = dropdownLabelRef?.current;
      const listRef = dropdownListRef?.current;

      const goToNext = (current: HTMLElement) => {
        const next = (current?.nextElementSibling as HTMLElement) || null;
        if (next) {
          next.focus();
        } else if (labelRef) {
          labelRef.focus();
        }
      };
      const goToPrevious = (current: HTMLElement) => {
        const prev = (current?.previousElementSibling as HTMLElement) || null;
        if (prev) {
          prev.focus();
        } else if (labelRef) {
          labelRef.focus();
        }
      };
      const goToFirst = () => {
        const lis = listRef?.querySelectorAll("li");
        (lis?.[0] as HTMLElement)?.focus()
      }
      const goToLast = () => {
        const lis = listRef?.querySelectorAll("li");
        (lis?.[lis?.length - 1] as HTMLElement)?.focus()
      }

      if (target?.contains(labelRef)) {
        if (enterIsPressed(e) || downArrowIsPressed(e)) {
          setOpen(true);
          goToFirst();
          e?.preventDefault();
        } else if (upArrowIsPressed(e)) {
          toggleSortingDropdown();
          goToLast();
          e?.preventDefault();
        }
      } else if (listRef?.contains(target)) {
        if (escIsPressed(e)) {
          blurField();
        } else if (enterIsPressed(e)) {
          setSelectedValue(target?.getAttribute("data-value") || "");
          setOpen(false);
          labelRef?.focus();
        } else if (downArrowIsPressed(e) || (tabIsPressed(e) && !e.shiftKey)) {
          e.preventDefault();
          goToNext(target);
        } else if (upArrowIsPressed(e) || (tabIsPressed(e) && e.shiftKey)) {
          e.preventDefault();
          goToPrevious(target);
        }
      }
    }, [dropdownLabelRef, dropdownListRef]);

    const onOptionClick = option => {
      onSelection(option);
      setSelectedValue(option?.value);
      setOpen(false);
      dropdownLabelRef?.current?.focus();
    };

    const clearIconAdornment: React.JSX.Element = (
      // [WAT-12043 / YFH-AMP117] Caret is decorative — the parent
      // TextField is already keyboard-operable, and its aria-label / label
      // provide the accessible name. Announcing "Click to open dropdown"
      // duplicates that intent and misleads keyboard users (there is no
      // click, they use Enter/Space).
      <CardMedia
        alt=""
        component="img"
        src={mode === "dark" ? "https://www.directv.com/dtvassets/global/icons/2022/arrow-down_dk-bg.svg" : "https://www.directv.com/dtvassets/global/icons/2022/arrow-down_lt-bg.svg"}
        sx={{
          height: "24px",
          width: "24px",
          position: 'absolute',
          right: "14px",
          top: "50%",
          transform: `translate(0, -50%) ${open ? "rotate(180deg)" : "rotate(0deg)"} !important`,
          color: "#000000",
          opacity: 1,
          ...hideSelectionState && {
            opacity: 0,
            pointerEvents: "none"
          },
          transition: "transform 200ms cubic-bezier(0.4, 0, 0.6, 1) 50ms, opacity 200ms cubic-bezier(0.42, 0, 0.58, 1)"
        }}
      />
    );

    const commonBoxStyles = {
      textOverflow: "ellipsis",
      textWrap: "nowrap",
      boxSizing: "border-box",
      cursor: "pointer",
      fontSize: "16px"
    };

    return (
      <ThemeProvider theme={FusionTheme}>
      <FormControl
        fullWidth
        error={error}
        id="formcontrolid"
        sx={{
          pointerEvents: disabled || !isVisible ? "none" : "",
          width: width || "270px",
          minWidth: width || "270px",
          ...styles.FormControl
        }}
        onKeyDown={handleKeyDown}
        {...FormControlProps}
      >
        <Box ref={clickOutsideComponentRef}>
          <>
            <TextField
              autoComplete="off"
              data-value={selectedValue}
              id={`${id}-txt-fld-dd`}
              inputRef={dropdownLabelRef}
              label={label}
              tabIndex={isVisible && !hideSelectionState ? 0 : -1}
              variant="outlined"
              InputLabelProps={{
                shrink: shrinkLabelActive,
                sx: styles.InputLabel,
                style: styles.InputLabelStyle
              }}
              InputProps={{
                id: "multi-select-input",
                readOnly: true,
                endAdornment: clearIconAdornment,
                value: `${prefix || ''}${selectedValueLabel}`,
                // [WAT-12043 / YFH-AMP117] Keep the field's label ("Select
                // your county" etc.) in the accessible name AT ALL TIMES
                // so voice-control users can still target the field while
                // options are loading (WCAG 2.5.3 Label in Name). Loading
                // state is exposed via aria-busy on `inputProps` below —
                // not by replacing the label with "Loading options".
                "aria-label": `${prefix || ''}${selectedValueLabel} selected`,
                tabIndex: isVisible && !hideSelectionState ? 0 : -1
              }}
              inputProps={{
                // [WAT-12043 / YFH-AMP117] Accessible name is the field's
                // purpose (e.g. "Select your county"), not a state phrase.
                // The floating <label> provides that name — mirroring it
                // here as aria-label keeps parity when the label is hidden
                // (small screens) and covers the empty-selection case
                // where the previous string collapsed to " option
                // selected".
                //
                // Loading state is signalled via `aria-busy` rather than
                // by replacing the aria-label with "Loading options" —
                // that swap removed the field's own name from the
                // accessible name and broke WCAG 2.5.3 Label in Name. The
                // field's label stays put; `aria-busy="true"` tells
                // assistive tech "this control is loading" without
                // touching its name.
                "aria-label": label,
                "aria-busy": !!isLoadingOptions
              }}
              sx={{
                ...commonBoxStyles,
                ...styles.InputContainer,
                ...hideSelectionState && {
                  pointerEvents: "none"
                }
              }}
              onClick={toggleSortingDropdown}
              // onKeyDown={handleKeyDown}
              fullWidth
              {...TextFieldProps}
            />

            {!hideSelectionState &&
              <Box
                id={`${id}-options-ctnr`}
                ref={dropdownListRef}
                sx={{
                  ...styles.MenuContainer,
                  ...(open && options?.length > 0 ? {
                    width: dropdownLabelRef?.current ? `${dropdownLabelRef?.current?.getBoundingClientRect()?.width}px` : '100%',
                    maxHeight: maxVisible !== undefined ?
                      typeof maxVisible === "number" ?
                        `${(maxVisible * 48) + 2}px` : // maxVisible * # items + border
                        maxVisible :
                      '240px',
                    border: '1px solid #8D8D8D',
                    opacity: 1,
                    // opacity transition in middle of timing
                    // width starts fast, ends slow
                    // height starts slow, ends fast
                    transition: 'opacity 294ms cubic-bezier(0.4, 0, 0.2, 1), width 146ms cubic-bezier(0.6, 0, 0.4, 1), max-height 196ms cubic-bezier(0.4, 0, 0.2, 1)'
                  } : {
                    width: "0px",
                    maxHeight: "0px",
                    border: 'none',
                    opacity: 0,
                    pointerEvents: "none",
                    // opacity transition in middle of timing
                    // width starts slow, ends fast
                    // height starts fast, ends slow
                    transition: 'opacity 294ms cubic-bezier(0.6, 0, 0.4, 1), width 146ms cubic-bezier(0.4, 0, 0.2, 1), max-height 196ms cubic-bezier(0.2, 0, 0.4, 1)'
                  })
                }}
                onKeyDown={handleKeyDown}
              >
                {options && options.map((option, index) => (
                  <ListItemButton
                    aria-label={option.label}
                    aria-selected={value === option?.value ? "true" : "false"}
                    component="li"
                    data-value={option.value}
                    disabled={option?.disabled}
                    id={option.value}
                    key={index}
                    role="option"
                    selected={value === option?.value}
                    tabIndex={isVisible ? 0 : -1}
                    value={option.value}
                    sx={{
                      ...commonBoxStyles,
                      ...styles.MenuItem,
                      width: "100% !important",
                      minHeight: open ? "48px" : "0px"
                    }}
                    onClick={() => onOptionClick(option)}
                  >
                    <Box className="text-container" sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      width: "100%"
                    }}>
                      <ListItemText
                        primary={option.label}
                        sx={{
                          flex: "unset",
                          padding: open ? "5px 3px" : 0,
                          marginLeft: "-3px",
                          borderRadius: "4px",
                          margin: 0,
                          transition: "background-color 100ms ease, text-decoration 100ms ease"
                          // 'opacity 294ms cubic-bezier(0.4, 0, 0.2, 1), transform 196ms cubic-bezier(0.4, 0, 0.2, 1)''
                        }}
                      />
                    </Box>
                  </ListItemButton>
                ))}
              </Box>
            }
          </>
        </Box>
        {showHelperMessage && (
          customHelperMessage !== undefined ?
            customHelperMessage :
            <HelperMessage
                error={error}
                iconVisible={helperMessageIcon && !disabled}
                message={helperMessageText}
                mode={mode}
              />
        )}
      </FormControl>
      </ThemeProvider>
    );
  } catch (_error) {
    return <></>;
  }
}
